import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  input,
  output,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslatePipe } from '@ngx-translate/core';
import { ShiftClientCommentWithResolvedClient } from '@wilson/interfaces';
import { PipesModule } from '@wilson/pipes';
import { NzInputModule } from 'ng-zorro-antd/input';
import { debounceTime } from 'rxjs';

const COMMENT_CHANGE_DEBOUNCE_MS = 400;

export const CLIENT_COMMENT_MAX_LENGTH = 450;

@Component({
  selector: 'wilson-shift-client-comment',
  templateUrl: './client-comment.component.html',
  styleUrl: './client-comment.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TranslatePipe, NzInputModule, ReactiveFormsModule, PipesModule],
})
export class ClientCommentComponent {
  private readonly destroyRef = inject(DestroyRef);

  readonly shiftComment =
    input.required<ShiftClientCommentWithResolvedClient>();
  readonly commentChange = output<string>();

  protected readonly maxLength = CLIENT_COMMENT_MAX_LENGTH;
  protected readonly clientName = computed(
    () => this.shiftComment().client?.name ?? null,
  );
  protected readonly control = new FormControl('', {
    nonNullable: true,
    validators: [
      Validators.required,
      Validators.maxLength(CLIENT_COMMENT_MAX_LENGTH),
    ],
  });

  constructor() {
    effect(() => {
      if (!this.control.dirty) {
        this.control.setValue(this.shiftComment().comment, {
          emitEvent: false,
        });
      }
    });

    this.control.valueChanges
      .pipe(
        debounceTime(COMMENT_CHANGE_DEBOUNCE_MS),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((value) => {
        if (this.control.valid) {
          this.commentChange.emit(value.trim());
        }
      });
  }
}
